import {environment} from '@env'
import {Injectable} from '@angular/core'
import {UserDetailModel} from '@domain/user'
import {RoleType} from '@domain/authorization'
import {BehaviorSubject} from 'rxjs'


const TIMEOUT = 10 * 3600 // timeout in seconds

/**
 * Service for storing user data and session information.
 * This implementation uses in-memory storage instead of sessionStorage for security.
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  user!: UserDetailModel

  // In-memory storage for user data
  private readonly userStore = new BehaviorSubject<UserDetailModel | null>(null)

  // In-memory storage for session timeout
  private readonly timeoutStore = new BehaviorSubject<number>(0)


  constructor() {
    // Initialize the timeout store with a default value
    this.setTimeout()
  }


  clean() {
    // Clear in-memory storage
    this.userStore.next(null)
    this.timeoutStore.next(0)
  }


  saveLoginUser(user: UserDetailModel) {
    this.removeLoginUser(user)
    if (user) {
      // Store user in memory
      this.userStore.next(user)
      this.user = user
      this.setTimeout()
    }
  }


  removeLoginUser(user: any) {
    // Clear user and timeout from memory
    this.userStore.next(null)
    this.timeoutStore.next(0)
  }


  getLoginUser(): any {
    // Get user from memory
    return this.userStore.getValue()
  }


  getLoginUserId(): any {
    const user = this.getLoginUser()
    if (user) {
      return user.id
    }
    return null
  }


  hasPermission(roles: RoleType[]): boolean {
    return this.isLoggedIn(this.user?.login) && this.hasOneOfRoles(roles)
  }


  hasOneOfRoles(roles: RoleType[]): boolean {
    const userRoles = this.getLoginRoles()
    return userRoles && roles.some(role => userRoles.includes(role))
  }


  getLoginRoles(): string[] {
    const user = this.getLoginUser()
    if (user) {
      return user.roles
    }
    return []
  }


  setTimeout() {
    const maxMillis = (environment.sessionTimeout || TIMEOUT) * 1000
    const tout = Date.now() + maxMillis
    // Store timeout in memory
    this.timeoutStore.next(tout)
  }


  getTimeout(): number {
    // Get timeout from memory
    return this.timeoutStore.getValue()
  }


  isTimedOut(): boolean {
    const tout = this.getTimeout()
    const now = Date.now()
    // console.debug('storage-service#isTimedOut: now=', now, ', tout=', tout, ', tout-now=', tout - now, ', isTimedOut=', (tout - now) < 0)
    if (tout < now) {
      this.clean()
      return true
    }
    return false
  }


  isLoggedIn(login: string): boolean {
    if (!login || !this.exist(login)) {
      return false
    }
    if (this.isTimedOut()) {
      return false
    }
    const user = this.getLoginUser()
    return !!user
  }


  exist(login: string): boolean {
    // Check if user exists in memory
    const user = this.getLoginUser()
    return !!user && user.login === login
  }
}
